import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

export interface ServiceConfig {
  name: string;
  icon: string;
  tagline: string;
  description: string;
  benefits: { icon: string; title: string; desc: string }[];
  faqs: { q: string; a: string }[];
  brands: string[];
  price: string;
  warranty: string;
}

const ServiceLandingPage: React.FC<{ config: ServiceConfig }> = ({ config }) => (
  <>
    <Header />
    <main className="bg-gray-50 min-h-screen">

      {/* Hero */}
      <section className="bg-gradient-to-br from-minto-600 to-minto-800 text-white py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-5xl mb-4">{config.icon}</div>
          <h1 className="text-3xl font-bold mb-3">{config.name} in Mumbai</h1>
          <p className="text-minto-100 text-lg mb-6">{config.tagline}</p>
          <div className="flex flex-wrap justify-center gap-3 mb-8 text-sm">
            <span className="bg-white/15 rounded-full px-4 py-1.5">⚡ Same Day Service</span>
            <span className="bg-white/15 rounded-full px-4 py-1.5">🛡️ {config.warranty} Warranty</span>
            <span className="bg-white/15 rounded-full px-4 py-1.5">🏠 Doorstep Repair</span>
            <span className="bg-white/15 rounded-full px-4 py-1.5">💰 Starting ₹{config.price}</span>
          </div>
          <Link
            to="/"
            className="inline-block bg-white text-minto-700 font-bold px-8 py-3 rounded-full shadow hover:shadow-lg transition-shadow"
          >
            Book Repair Now
          </Link>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-10">

        {/* About */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-3">{config.name} — What We Do</h2>
          <p className="text-gray-600 leading-relaxed">{config.description}</p>
        </section>

        {/* Benefits */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Why Choose minto?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {config.benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm flex gap-4 items-start">
                <span className="text-2xl">{b.icon}</span>
                <div>
                  <p className="font-semibold text-gray-800 mb-1">{b.title}</p>
                  <p className="text-sm text-gray-500">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Supported Brands */}
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Supported Brands</h2>
          <div className="flex flex-wrap gap-2">
            {config.brands.map((b) => (
              <span key={b} className="bg-minto-50 text-minto-700 text-sm font-medium px-3 py-1 rounded-full border border-minto-100">
                {b}
              </span>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {config.faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="font-semibold text-gray-800 mb-1">Q: {faq.q}</p>
                <p className="text-sm text-gray-500">A: {faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-minto-600 text-white rounded-2xl p-8 text-center shadow-lg">
          <h2 className="text-2xl font-bold mb-2">Ready to get it fixed?</h2>
          <p className="text-minto-100 mb-6">Certified technician at your doorstep — today.</p>
          <Link
            to="/"
            className="inline-block bg-white text-minto-700 font-bold px-8 py-3 rounded-full shadow hover:shadow-lg transition-shadow"
          >
            Book {config.name} Now
          </Link>
        </section>

      </div>
    </main>
    <Footer />
  </>
);

export default ServiceLandingPage;
